import { useEffect, useState } from "react";
import { Pool } from "@/types";
import AddPoolForm from "./AddPoolForm";
import { Pencil, Trash2 } from "lucide-react";
import { Button } from "./ui/button";
import { PoolRepository } from "@/infrastructure/repositories/PoolRepository";

type FeedbackType = "success" | "error";

interface CreatePoolPageProps {
  pools: Pool[];
  onPoolsChange: (pools: Pool[]) => void;
}

const PAGE_SIZE = 12;

export function CreatePoolPage({
  pools,
  onPoolsChange,
}: CreatePoolPageProps) {
  const poolRepo = new PoolRepository();

  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editingPool, setEditingPool] = useState<Pool | null>(null);
  const [poolToDelete, setPoolToDelete] = useState<Pool | null>(null);

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [feedback, setFeedback] = useState<{
    type: FeedbackType;
    message: string;
  } | null>(null);

  // ----------------------------------
  // LOAD POOLS
  // ----------------------------------
  useEffect(() => {
    const loadPools = async () => {
      setLoading(true);

      const apiPools = await poolRepo.getPoolList(page, PAGE_SIZE);

      if (page === 1) {
        onPoolsChange(apiPools);
      } else {
        onPoolsChange([...pools, ...apiPools]);
      }

      if (apiPools.length < PAGE_SIZE) {
        setHasMore(false);
      }

      setLoading(false);
    };

    loadPools();
  }, [page]);

  // ----------------------------------
  // CLOSE MODAL ON ESC
  // ----------------------------------
  useEffect(() => {
    if (!isAddOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsAddOpen(false);
        setEditingPool(null);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isAddOpen]);

  // ----------------------------------
  // CONFIRM DELETE
  // ----------------------------------
  const confirmDelete = async () => {
    if (!poolToDelete) return;

    setSaving(true);

    const success = await poolRepo.deletePool(poolToDelete.id);

    if (success) {
      onPoolsChange(pools.filter(p => p.id !== poolToDelete.id));
      setFeedback({
        type: "success",
        message: "Pool deleted successfully",
      });
    } else {
      setFeedback({
        type: "error",
        message: "Failed to delete pool",
      });
    }

    setPoolToDelete(null);
    setSaving(false);
  };

  // ----------------------------------
  // CREATE / UPDATE
  // ----------------------------------
  const handleSubmit = async (pool: Pool) => {
    setSaving(true);

    let success = false;

    if (editingPool) {
      success = await poolRepo.updatePool({
        ...pool,
        id: editingPool.id,
      });
    } else {
      success = await poolRepo.createPool(pool);
    }

    if (!success) {
      setFeedback({
        type: "error",
        message: editingPool
          ? "Failed to update pool"
          : "Failed to create pool",
      });
      setSaving(false);
      return;
    }

    // Reload from page 1 after mutation
    setPage(1);
    setHasMore(true);

    const refreshed = await poolRepo.getPoolList(1, PAGE_SIZE);
    onPoolsChange(refreshed);

    setFeedback({
      type: "success",
      message: editingPool
        ? "Pool updated successfully"
        : "Pool created successfully",
    });

    setEditingPool(null);
    setIsAddOpen(false);
    setSaving(false);
  };

  // ----------------------------------
  // UI
  // ----------------------------------
  return (
    <div className="max-w-7xl mx-auto px-6 py-10 text-foreground">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-semibold">Pools</h1>
          <p className="text-sm text-muted-foreground">
            Manage swimming pools and academies
          </p>
        </div>

        <Button onClick={() => setIsAddOpen(true)} disabled={saving}>
          Add Pool
        </Button>
      </div>

      {loading && page === 1 ? (
        <div className="text-center text-muted-foreground">
          Loading pools...
        </div>
      ) : pools.length === 0 ? (
        <div className="border rounded-xl p-10 text-center text-muted-foreground">
          No pools added yet.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {pools.map(pool => (
              <div
                key={pool.id}
                className="bg-background border rounded-xl overflow-hidden hover:shadow-lg transition flex flex-col"
              >
                <img
                  src={pool.image}
                  alt={pool.poolname}
                  className="h-40 w-full object-cover"
                />

                <div className="p-4 flex flex-col flex-1">
                  <h3 className="font-semibold">{pool.poolname}</h3>

                  <p className="text-sm text-muted-foreground">
                    {pool.city}, {pool.state}
                  </p>

                  <div className="text-xs text-muted-foreground mt-2">
                    <div>{pool.type}</div>
                    <div>{pool.timing}</div>
                  </div>

                  <div className="mt-4 flex justify-end gap-2">
                    <button
                      disabled={saving}
                      onClick={() => {
                        setEditingPool(pool);
                        setIsAddOpen(true);
                      }}
                      className="p-2 border rounded-md hover:bg-muted"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>

                    <button
                      disabled={saving}
                      onClick={() => setPoolToDelete(pool)}
                      className="p-2 border rounded-md hover:bg-red-500/10"
                    >
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {hasMore && (
            <div className="mt-10 flex justify-center">
              <Button
                variant="outline"
                onClick={() => setPage(p => p + 1)}
                disabled={loading}
              >
                {loading ? "Loading..." : "Load more"}
              </Button>
            </div>
          )}
        </>
      )}

      {/* DELETE CONFIRM DIALOG */}
      {poolToDelete && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-background rounded-2xl border shadow-xl p-6">
            <h3 className="text-lg font-semibold mb-3">Delete pool</h3>

            <p className="text-sm text-muted-foreground mb-6">
              Are you sure you want to delete{" "}
              <span className="font-medium text-foreground">
                {poolToDelete.poolname}
              </span>
              ?
            </p>

            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setPoolToDelete(null)}
                disabled={saving}
              >
                Cancel
              </Button>

              <Button
                variant="destructive"
                onClick={confirmDelete}
                disabled={saving}
              >
                {saving ? "Deleting..." : "Delete"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* SUCCESS / ERROR DIALOG */}
      {feedback && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-background rounded-2xl border shadow-xl p-6 text-center">
            <h3 className="text-lg font-semibold mb-2">
              {feedback.type === "success" ? "Success" : "Error"}
            </h3>

            <p className="text-sm text-muted-foreground mb-6">
              {feedback.message}
            </p>

            <Button onClick={() => setFeedback(null)} className="w-full">
              OK
            </Button>
          </div>
        </div>
      )}

      {/* ADD / EDIT MODAL */}
      {isAddOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onClick={() => {
            setIsAddOpen(false);
            setEditingPool(null);
          }}
        >
          <div
            className="w-full max-w-xl max-h-[90vh] bg-background rounded-2xl border shadow-xl flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            <div className="p-6 border-b flex justify-between items-center">
              <h3 className="text-xl font-semibold">
                {editingPool ? "Edit Pool" : "Add Pool"}
              </h3>

              <button
                onClick={() => {
                  setIsAddOpen(false);
                  setEditingPool(null);
                }}
                className="text-muted-foreground hover:text-foreground text-xl"
              >
                ×
              </button>
            </div>

            <div className="p-6 overflow-y-auto">
              <AddPoolForm
                initialPool={editingPool}
                loading={saving}
                onCancel={() => {
                  setEditingPool(null);
                  setIsAddOpen(false);
                }}
                onSubmit={handleSubmit}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
